import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { BodyText, SubBodyText } from '../Text';
import { Variant } from '../../utils/constants';
import Wrapper from '../Wrapper';
import Row from '../Container/Row';
import Icon from '../Icon';

const MAX_HEIGHT = 365;
const SCROLL_SPACE = 40;

const HoverSource = {
  MouseOver: 'MouseOver',
  KeyBoard: 'KeyBoard',
};

const SelectItems = styled(Wrapper)`
  outline: none;
  z-index: 1;
`;

SelectItems.defaultProps = {
  position: ['relative', 'absolute'],
  border: '1px solid',
  borderColor: 'greyLight',
  borderRadius: 4,
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.08)',
  background: 'white',
  marginTop: 3,
  width: 1,
  padding: 0,
  paddingTop: '7px',
  overflow: 'auto',
  maxHeight: MAX_HEIGHT,
  minHeight: SCROLL_SPACE,
};
SelectItems.displayName = 'SelectItems';

const hoverCss = css`
  &:hover {
    background: #f8f8f8;
  }
`;

const Option = styled(Row)`
  cursor: pointer;
  ${({ hoverSource }) =>
    hoverSource === HoverSource.MouseOver ? hoverCss : ''};
  background: ${({ isHovered }) => (isHovered ? '#f8f8f8' : 'transparent')};
`;
Option.defaultProps = {
  paddding: 0,
  paddingX: 16,
  paddingY: 10,
  justifyContent: 'space-between',
};

Option.displayName = 'Option';

const RightText = props => <SubBodyText color="greyDark" {...props} />;

RightText.displayName = 'RightText';

const OptionText = ({ isSelected, ...restProps }) =>
  isSelected ? (
    <BodyText
      lineHeight="20px"
      fontWeight={500}
      color="secondary"
      {...restProps}
    />
  ) : (
    <BodyText lineHeight="20px" fontWeight={400} {...restProps} />
  );

OptionText.propTypes = {
  isSelected: PropTypes.bool,
};

OptionText.displayName = 'OptionText';

const scrollToRef = (itemList, newTop) => {
  if (itemList && itemList.scrollTo) {
    itemList.scrollTo({
      behavior: 'smooth',
      top: newTop,
    });
  }
};

const processScrollToItem = (itemListRef, highlightedItemIndex) => {
  const itemList = itemListRef.current;
  if (itemList) {
    const maxItemsLength = Math.ceil(MAX_HEIGHT / (SCROLL_SPACE + 2));
    const rangeItems = highlightedItemIndex - maxItemsLength;
    const { scrollTop: fromTopPos } = itemList;
    const toTopPos = fromTopPos + MAX_HEIGHT - SCROLL_SPACE;
    const currentTopPos = highlightedItemIndex * SCROLL_SPACE;
    const supportToScroll = !(
      currentTopPos < toTopPos && currentTopPos > fromTopPos
    );

    if (supportToScroll) {
      const newTop =
        (currentTopPos > fromTopPos ? rangeItems + 1 : highlightedItemIndex) *
        SCROLL_SPACE;
      scrollToRef(itemList, newTop);
    }
  }
};

const getItem = (propValue, items) =>
  items.find(({ value }) => value === propValue);

const PickerItems = ({
  items,
  value: propValue,
  showPicker,
  onValueChange,
  showRightText,
  ...restProps
}) => {
  const selectItemRef = React.useRef();

  const selectedItemIndex = propValue
    ? items.indexOf(getItem(propValue, items))
    : -1;

  const [itemIndex, setItemIndex] = React.useState(selectedItemIndex);
  const [showPopup, setShowPopup] = React.useState(showPicker);
  const [hoverSource, setHoverSource] = React.useState(HoverSource.KeyBoard);

  React.useEffect(() => {
    if (selectItemRef.current) {
      selectItemRef.current.focus();
      if (showPicker) {
        processScrollToItem(selectItemRef, itemIndex);
      }
    }
    // eslint-disable-next-line
  });

  React.useEffect(() => {
    setShowPopup(showPicker);
    if (showPicker) {
      setItemIndex(selectedItemIndex);
    }
  }, [showPicker, selectedItemIndex]);

  const handelInputKeyDown = event => {
    event.preventDefault();
    setHoverSource(HoverSource.KeyBoard);
    switch (event.key) {
      case 'ArrowDown': {
        if (itemIndex < items.length - 1) {
          setItemIndex(itemIndex + 1);
        }
        break;
      }
      case 'ArrowUp': {
        if (itemIndex > 0) {
          setItemIndex(itemIndex - 1);
        }
        break;
      }
      case 'Enter': {
        const correctIndex = itemIndex < 0 ? selectedItemIndex : itemIndex;
        onValueChange(items[correctIndex], correctIndex);
        break;
      }
      case 'Tab': {
        setShowPopup(false);
        onValueChange(items[selectedItemIndex], selectedItemIndex);
        break;
      }
      default:
        break;
    }
  };

  const handelMouseMove = itemIndex => event => {
    event.preventDefault();
    setItemIndex(itemIndex);
    setHoverSource(HoverSource.MouseOver);
  };

  const handleOptionClick = (item, index) => {
    setShowPopup(false);
    setItemIndex(index);
    onValueChange(item, index);
  };

  return (
    <SelectItems
      {...restProps}
      display={showPopup ? 'block' : 'none'}
      onKeyDown={handelInputKeyDown}
      tabIndex="0"
      ref={selectItemRef}
    >
      {showPopup &&
        items.map((item, index) => {
          const isSelected = selectedItemIndex === index;
          return (
            <Option
              key={index}
              onMouseMove={handelMouseMove(index)}
              onClick={() => handleOptionClick(item, index)}
              itemIndex={itemIndex}
              isHovered={itemIndex === index}
              hoverSource={hoverSource}
            >
              <OptionText isSelected={isSelected}>{item.label}</OptionText>
              {showRightText && <RightText>{item.rightLabel}</RightText>}
              {!showRightText && isSelected && (
                <Icon icon="CheckIcon" variant={Variant.Success} />
              )}
            </Option>
          );
        })}
    </SelectItems>
  );
};

PickerItems.propTypes = {
  items: PropTypes.array,
  value: PropTypes.string,
  showPicker: PropTypes.bool,
  showRightText: PropTypes.bool,
  onValueChange: PropTypes.func,
};

PickerItems.displayName = 'PickerItemsDesktop';

export default PickerItems;
